import React, { useRef, useEffect } from 'react';
import { gsap } from '../../lib/gsapSetup';
import { useTranslation } from '../../i18n/LanguageContext';
import communicationImage from '../../assets/images/communication.webp';
import enginsImage from '../../assets/images/engins.webp';
import benefitsImage from '../../assets/images/plus_de_benefices.webp';
import remorquageImage from '../../assets/images/remorquage.webp';
import transportationImage from '../../assets/images/transportation.webp';
import transporteurImage from '../../assets/images/transporteur.webp';
import visibilityImage from '../../assets/images/visibility.webp';

/**
 * FEATURES — Asymmetric mosaic showcase.
 *
 * 12-column grid with zigzag card sizing for visual hierarchy.
 * Cards 0–5 are image overlay cards; Card 6 uses a unique split layout
 * (image + navy text panel). Scroll-triggered reveals, parallax imagery,
 * decorative watermark numbers, gold accent bars, and lift + glow hover.
 */

const CARDS = [
  {
    id: 1,
    titleKey: 'features.0.title',
    descKey: 'features.0.desc',
    image: transportationImage,
  },
  {
    id: 2,
    titleKey: 'features.1.title',
    descKey: 'features.1.desc',
    image: communicationImage,
  },
  {
    id: 3,
    titleKey: 'features.2.title',
    descKey: 'features.2.desc',
    image: remorquageImage,
  },
  {
    id: 4,
    titleKey: 'features.3.title',
    descKey: 'features.3.desc',
    badgeKey: 'features.3.badge',
    image: enginsImage,
  },
  {
    id: 5,
    titleKey: 'features.4.title',
    descKey: 'features.4.desc',
    image: transporteurImage,
  },
  {
    id: 6,
    titleKey: 'features.5.title',
    descKey: 'features.5.desc',
    image: visibilityImage,
  },
  {
    id: 7,
    titleKey: 'features.6.title',
    descKey: 'features.6.desc',
    image: benefitsImage,
  },
];

/*
 * Grid placement per card (md uses 12-col grid).
 * Zigzag: 8/4 → 4/8 → 6/6 → 12 (split)
 */
const LAYOUT = [
  /* 0 */ 'md:col-span-8  h-[280px] sm:h-[360px] lg:h-[440px]',
  /* 1 */ 'md:col-span-4  h-[280px] sm:h-[360px] lg:h-[440px]',
  /* 2 */ 'md:col-span-4  h-[260px] sm:h-[320px] lg:h-[380px]',
  /* 3 */ 'md:col-span-8  h-[260px] sm:h-[320px] lg:h-[380px]',
  /* 4 */ 'md:col-span-6  h-[260px] sm:h-[300px] lg:h-[360px]',
  /* 5 */ 'md:col-span-6  h-[260px] sm:h-[300px] lg:h-[360px]',
  /* 6 */ 'md:col-span-12 h-[340px] sm:h-[280px] lg:h-[320px]',
];

const fmtIdx = (i) => String(i + 1).padStart(2, '0');

export default function Features() {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const headingRef = useRef(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      /* ── Heading reveal ── */
      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: { trigger: headingRef.current, start: 'top 85%' },
          },
        );
      }

      /* ── Card animations ── */
      const cards = sectionRef.current.querySelectorAll('.feat-card');
      cards.forEach((card, i) => {
        gsap.fromTo(
          card,
          { opacity: 0, y: 70, scale: 0.97 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.9,
            delay: i * 0.04,
            ease: 'power3.out',
            scrollTrigger: { trigger: card, start: 'top 92%' },
          },
        );

        const img = card.querySelector('.feat-img');
        if (img) {
          gsap.fromTo(
            img,
            { scale: 1.12 },
            {
              scale: 1,
              duration: 1.4,
              ease: 'power2.out',
              scrollTrigger: { trigger: card, start: 'top 92%' },
            },
          );
          gsap.to(img, {
            yPercent: -8,
            ease: 'none',
            scrollTrigger: {
              trigger: card,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          });
        }

        const content = card.querySelector('.feat-content');
        if (content) {
          gsap.fromTo(
            content,
            { opacity: 0, y: 24 },
            {
              opacity: 1,
              y: 0,
              duration: 0.7,
              delay: 0.12,
              ease: 'power2.out',
              scrollTrigger: { trigger: card, start: 'top 84%' },
            },
          );
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  /* ── Render a regular image-overlay card ── */
  const renderImageCard = (card, i) => (
    <div
      key={card.id}
      className={`feat-card group relative overflow-hidden rounded-2xl lg:rounded-3xl cursor-default
        col-span-1 ${LAYOUT[i]}
        border border-transparent transition-all duration-500
        hover:border-secondary/25 hover:shadow-[0_12px_48px_rgba(253,190,52,0.13)] hover:-translate-y-1`}
    >
      {/* Image */}
      <div className='absolute inset-0 overflow-hidden'>
        <img
          src={card.image}
          alt={t(card.titleKey)}
          className='feat-img absolute inset-0 w-full h-[120%] object-cover will-change-transform
            transition-transform duration-700 ease-out group-hover:scale-[1.04]'
          loading='lazy'
        />
      </div>

      {/* Gradient */}
      <div
        className='absolute inset-0 bg-gradient-to-t from-primary via-primary/50 to-transparent
        opacity-80 transition-opacity duration-500 group-hover:opacity-[0.88]'
      />

      {/* Watermark number */}
      <span
        className='absolute top-3 right-4 sm:top-4 sm:right-5 font-display
          text-[4rem] sm:text-[5rem] lg:text-[6.5rem] font-bold leading-none
          text-white/[0.05] select-none pointer-events-none
          transition-all duration-600 group-hover:text-white/[0.1] group-hover:scale-110'
      >
        {fmtIdx(i)}
      </span>

      {/* Badge */}
      {card.badgeKey && (
        <div className='absolute top-4 left-4 sm:top-5 sm:left-5 z-10'>
          <span
            className='inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1
            text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-primary
            shadow-lg shadow-secondary/25'
          >
            <span className='h-1.5 w-1.5 rounded-full bg-primary/40 animate-pulse' />
            {t(card.badgeKey)}
          </span>
        </div>
      )}

      {/* Content */}
      <div className='feat-content absolute inset-x-0 bottom-0 p-5 sm:p-6 lg:p-8 z-10'>
        <span
          className='inline-block h-[3px] w-8 rounded-full bg-secondary mb-3
          transition-all duration-500 group-hover:w-14'
        />
        <h3
          className='font-display text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1.5
            transition-transform duration-500 group-hover:translate-x-1'
          style={{ textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}
        >
          {t(card.titleKey)}
        </h3>
        <p
          className='text-xs sm:text-sm lg:text-[0.94rem] text-white/80 leading-relaxed max-w-lg'
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
        >
          {t(card.descKey)}
        </p>
      </div>

      {/* Hover glow sweep */}
      <div
        className='absolute inset-0 opacity-0 group-hover:opacity-100
        transition-opacity duration-700 pointer-events-none
        bg-gradient-to-tr from-secondary/[0.06] via-transparent to-transparent'
      />
    </div>
  );

  /* ── Render the last card as a unique split layout ── */
  const renderSplitCard = (card, i) => (
    <div
      key={card.id}
      className={`feat-card group relative overflow-hidden rounded-2xl lg:rounded-3xl cursor-default
        col-span-1 ${LAYOUT[i]}
        border border-transparent transition-all duration-500
        hover:border-secondary/25 hover:shadow-[0_12px_48px_rgba(253,190,52,0.13)]
        flex flex-col sm:flex-row`}
    >
      {/* Image side */}
      <div className='relative w-full sm:w-[45%] h-[160px] sm:h-full overflow-hidden shrink-0'>
        <img
          src={card.image}
          alt={t(card.titleKey)}
          className='feat-img absolute inset-0 w-full h-[125%] object-cover
            transition-transform duration-700 group-hover:scale-105'
          loading='lazy'
        />
        {/* Directional fade into navy side */}
        <div className='absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-transparent to-primary/30' />
      </div>

      {/* Text side */}
      <div className='relative flex-1 bg-primary flex flex-col justify-center p-6 sm:p-8 lg:p-12 overflow-hidden'>
        {/* Watermark number */}
        <span
          className='absolute -top-2 right-4 sm:top-0 sm:right-6 font-display
            text-[5rem] sm:text-[7rem] lg:text-[9rem] font-bold leading-none
            text-white/[0.03] select-none pointer-events-none'
        >
          {fmtIdx(i)}
        </span>

        <div className='feat-content relative z-10'>
          <span
            className='inline-block h-[3px] w-10 rounded-full bg-secondary mb-4
            transition-all duration-500 group-hover:w-16'
          />
          <h3
            className='font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3
            transition-transform duration-500 group-hover:translate-x-1'
          >
            {t(card.titleKey)}
          </h3>
          <p className='text-sm sm:text-base text-white/65 leading-relaxed max-w-md'>
            {t(card.descKey)}
          </p>
        </div>

        {/* Decorative corner glow */}
        <div
          className='absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-secondary/[0.06] blur-3xl
          transition-all duration-700 group-hover:bg-secondary/[0.12]'
        />
      </div>
    </div>
  );

  return (
    <section
      id='features'
      ref={sectionRef}
      className='relative overflow-hidden bg-linx-bg py-20 sm:py-28 lg:py-36'
    >
      {/* Background decoration */}
      <div className='pointer-events-none absolute inset-0 overflow-hidden'>
        <div className='absolute -top-24 left-1/4 h-[500px] w-[500px] rounded-full bg-secondary/[0.06] blur-[120px]' />
        <div className='absolute top-1/2 right-0 h-[400px] w-[400px] rounded-full bg-primary/[0.04] blur-[100px]' />
        <div className='absolute bottom-0 left-0 h-80 w-80 rounded-full bg-secondary/[0.05] blur-[100px]' />
      </div>

      <div className='section-container relative z-10'>
        {/* Header */}
        <div className='mx-auto max-w-3xl text-center mb-14 lg:mb-20'>
          <span className='inline-flex rounded-full bg-primary/[0.08] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-primary'>
            {t('features.label')}
          </span>
          <h2
            ref={headingRef}
            className='mt-5 font-display text-section-title font-bold text-linx_text-primary'
          >
            {t('features.heading')}
          </h2>
        </div>

        {/* Asymmetric mosaic grid */}
        <div className='grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4 lg:gap-5'>
          {CARDS.map((card, i) =>
            i === CARDS.length - 1
              ? renderSplitCard(card, i)
              : renderImageCard(card, i),
          )}
        </div>
      </div>
    </section>
  );
}
